import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { AddCircleOutline, DeleteOutline } from "@material-ui/icons";
import { Stack } from "@mui/material";

import { i18n } from "../../translate/i18n";

const MIN_BRANCHES = 2;
const MAX_BRANCHES = 6;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  footerHint: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}));

const clampPercent = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  if (parsed < 0) return 0;
  if (parsed > 100) return 100;
  return Math.round(parsed);
};

const evenDistribution = (count) => {
  const safeCount = Math.max(MIN_BRANCHES, Math.min(MAX_BRANCHES, Number(count) || MIN_BRANCHES));
  const base = Math.floor(100 / safeCount);
  const remainder = 100 - base * safeCount;
  return Array.from({ length: safeCount }, (_, index) => base + (index < remainder ? 1 : 0));
};

const normalizeDistribution = (rawData) => {
  const fromDistribution = Array.isArray(rawData?.distribution)
    ? rawData.distribution.map(clampPercent).filter((val) => Number.isFinite(val))
    : [];

  if (fromDistribution.length >= MIN_BRANCHES) {
    return fromDistribution.slice(0, MAX_BRANCHES);
  }

  const legacyPercent = clampPercent(rawData?.percent);
  if (legacyPercent > 0 && legacyPercent < 100) {
    return [legacyPercent, 100 - legacyPercent];
  }

  return evenDistribution(MIN_BRANCHES);
};

const buildRandomizerPayload = (distribution) => ({
  percent: distribution[0],
  distribution,
});

const FlowBuilderRandomizerModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const [distribution, setDistribution] = useState(evenDistribution(MIN_BRANCHES));
  const [activeModal, setActiveModal] = useState(false);

  useEffect(() => {
    if (open === "edit") {
      setDistribution(normalizeDistribution(data?.data || {}));
      setActiveModal(true);
    } else if (open === "create") {
      setDistribution(evenDistribution(MIN_BRANCHES));
      setActiveModal(true);
    }

    return () => {
      isMounted.current = false;
    };
  }, [open, data]);

  const totalPercent = useMemo(
    () => distribution.reduce((acc, current) => acc + clampPercent(current), 0),
    [distribution]
  );

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleBranchPercentChange = (index, value) => {
    setDistribution((prev) =>
      prev.map((item, itemIndex) => (itemIndex === index ? clampPercent(value) : item))
    );
  };

  const handleAddBranch = () => {
    setDistribution((prev) => {
      if (prev.length >= MAX_BRANCHES) return prev;
      return evenDistribution(prev.length + 1);
    });
  };

  const handleRemoveBranch = (index) => {
    setDistribution((prev) => {
      if (prev.length <= MIN_BRANCHES) return prev;
      const next = prev.filter((_, itemIndex) => itemIndex !== index);
      return evenDistribution(next.length);
    });
  };

  const handleSave = async () => {
    if (!distribution.length || distribution.some((value) => clampPercent(value) <= 0)) {
      return toast.error(i18n.t("flowBuilder.modals.randomizer.errors.required"));
    }

    if (totalPercent !== 100) {
      return toast.error(i18n.t("flowBuilder.modals.randomizer.errors.maxPercent"));
    }

    const payload = buildRandomizerPayload(distribution.map(clampPercent));

    if (open === "edit") {
      onUpdate({
        ...data,
        data: payload,
      });
    } else if (open === "create") {
      onSave(payload);
    }

    handleClose();
  };

  return (
    <div className={classes.root}>
      <Dialog open={activeModal} onClose={handleClose} fullWidth maxWidth="sm" scroll="paper">
        <DialogTitle id="form-dialog-title">
          {open === "create"
            ? i18n.t("flowBuilder.modals.randomizer.titleCreate")
            : i18n.t("flowBuilder.modals.randomizer.titleEdit")}
        </DialogTitle>

        <DialogContent dividers>
          <Stack>
            {distribution.map((value, index) => (
              <div key={`random-branch-${index}`} className={classes.row}>
                <TextField
                  type="number"
                  variant="outlined"
                  size="small"
                  inputProps={{ min: 0, max: 100, step: 1 }}
                  label={`${i18n.t("flowBuilder.modals.randomizer.aria.slider")} ${index + 1}`}
                  value={value}
                  onChange={(event) => handleBranchPercentChange(index, event.target.value)}
                />
                <IconButton
                  onClick={() => handleRemoveBranch(index)}
                  disabled={distribution.length <= MIN_BRANCHES}
                  aria-label={i18n.t("common.delete")}
                >
                  <DeleteOutline />
                </IconButton>
              </div>
            ))}

            <Button
              onClick={handleAddBranch}
              variant="outlined"
              color="primary"
              startIcon={<AddCircleOutline />}
              disabled={distribution.length >= MAX_BRANCHES}
            >
              {i18n.t("common.add")}
            </Button>

            <Typography variant="body2" className={classes.footerHint}>
              {`${totalPercent}% / 100%`}
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            {i18n.t("common.cancel")}
          </Button>
          <Button type="button" color="primary" variant="contained" onClick={handleSave}>
            {open === "create" ? i18n.t("common.add") : i18n.t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlowBuilderRandomizerModal;
